import React, { useEffect, useState } from 'react';
import { Tooltip as MuiTooltip } from '@mui/material';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import { formatarNumero } from '../../utils/formatters';
import AvisoDadosDesatualizados from './AvisoDadosDesatualizados';

interface Indicador {
  chave: string;
  rotulo: string;
  pct: number | null;
  numerador: number;
  denominador: number;
  explicacao: string;
}

interface Qualidade {
  totalRegistros: number;
  indicadores: Indicador[];
}

interface QualidadeDadosProps {
  /** Query string dos filtros globais (sem '?'), a mesma do /pesca/stats. */
  filtrosQuery: string;
}

// Verde a partir de 80%, âmbar de 50 a 80, vermelho abaixo de 50, cinza sem base.
const corDoPercentual = (pct: number | null) => (pct === null ? '#b5bcc2' : pct >= 80 ? '#00b37e' : pct >= 50 ? '#e0a040' : '#f75a68');

const QualidadeDados: React.FC<QualidadeDadosProps> = ({ filtrosQuery }) => {
  const [dados, setDados] = useState<Qualidade | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    api.get(`/pesca/stats/qualidade?${filtrosQuery}`)
      .then((res) => { if (ativo) { setDados(res.data.data); setErro(null); } })
      // mantém os números anteriores (não apaga) e avisa que estão desatualizados
      .catch((e) => { if (ativo) setErro(e?.response?.data?.message || 'Falha ao calcular a qualidade dos dados.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [filtrosQuery, tentativa]);

  return (
    <div className={styles.sectionCard} style={{ marginTop: '10px', padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '0.9rem', color: '#333' }}>Qualidade dos dados</strong>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>
          {dados ? `${formatarNumero(dados.totalRegistros, 0)} registros no recorte atual` : ''} · quanto de cada informação está preenchida
        </span>
      </div>

      <AvisoDadosDesatualizados erro={!!erro} comDadosAnteriores={!!dados} detalhe={erro} carregando={carregando} onTentarDeNovo={() => setTentativa((t) => t + 1)} />

      {dados && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px 16px', opacity: carregando ? 0.6 : 1 }}>
          {/* "Local aproximado" não é exibido: é o complemento do exato e, em verde, passaria a ideia errada */}
          {dados.indicadores.filter((i) => i.chave !== 'local_aproximado').map((i) => (
            <MuiTooltip
              key={i.chave}
              arrow
              title={`${i.explicacao} ${i.pct === null ? 'Sem base neste recorte.' : `(${formatarNumero(i.numerador, 0)} de ${formatarNumero(i.denominador, 0)}${i.chave === 'mapa' ? ' kg' : ''})`}`}
            >
              <div style={{ cursor: 'help' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', fontSize: '0.74rem', color: '#555' }}>
                  <span>{i.rotulo}</span>
                  <strong style={{ color: corDoPercentual(i.pct) }}>{i.pct === null ? 'n/d' : `${formatarNumero(i.pct, i.pct % 1 === 0 ? 0 : 1)}%`}</strong>
                </div>
                <div style={{ height: '5px', background: '#eef1f3', borderRadius: '3px', marginTop: '3px' }}>
                  <div style={{ width: `${i.pct ?? 0}%`, height: '100%', background: corDoPercentual(i.pct), borderRadius: '3px' }} />
                </div>
              </div>
            </MuiTooltip>
          ))}
        </div>
      )}
      {!dados && !erro && <span style={{ fontSize: '0.8rem', color: '#999' }}>Calculando...</span>}
    </div>
  );
};

export default QualidadeDados;
